
import { Button } from "@/components/ui/button";
import { Coins, AlertTriangle, Plus } from "lucide-react";
import { useWallet } from "@/contexts/WalletContext";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format";
import { HStack } from "@/components/core";

import { useTranslation } from 'react-i18next';

interface WalletBalanceProps {
  showWarning?: boolean;
  className?: string;
}

export const WalletBalance = ({ showWarning = true, className }: WalletBalanceProps) => {
  const { t } = useTranslation('billing');
  const { balance, isLoading } = useWallet();
  const navigate = useNavigate();

  const formatBalance = (amount: number) => {
    return formatNumber(amount, { maximumFractionDigits: 0 });
  };

  const getStatusStyles = (amount: number) => {
    if (amount >= 100) {
      return {
        container: "design-glass-surface-elevated border-success\/20",
        text: "text-success-light",
        icon: "text-success"
      };
    }
    if (amount >= 50) {
      return {
        container: "design-glass-surface-elevated border-warning\/20",
        text: "text-warning-light",
        icon: "text-warning"
      };
    }
    return {
      container: "design-glass-surface-elevated border-error\/20",
      text: "text-error-light",
      icon: "text-error"
    };
  };

  if (isLoading) {
    return (
      <HStack align="center" gap="sm" className={cn(className)}>
        <div className="h-8 px-token-sm design-glass-surface-elevated rounded-token-card border border-white/10 animate-pulse">
          <HStack align="center" gap="sm" className="h-full">
            <div className="w-4 h-4 bg-white/20 rounded-full" />
            <div className="w-12 h-3 bg-white/20 rounded" />
          </HStack>
        </div>
      </HStack>
    );
  }

  const coins = balance || 0;
  const isLowBalance = coins < 50;
  const styles = getStatusStyles(coins);

  return (
    <HStack align="center" gap="sm" className={cn(className)}>
      {showWarning && isLowBalance && (
        <AlertTriangle className="h-4 w-4 text-warning" />
      )}
      
      <Link to="/wallet">
        <div 
          className={cn(
            "flex items-center gap-token-sm px-token-sm py-1.5 rounded-token-card border transition-all duration-200 hover:border-opacity-40 cursor-pointer",
            "backdrop-blur-md",
            styles.container
          )}
        >
          <Coins className={cn("h-4 w-4", styles.icon)} />
          <span className={cn("text-sm font-semibold tabular-nums", styles.text)}>
            {formatBalance(coins)}
          </span>
        </div>
      </Link>

      {isLowBalance && (
        <Button 
          size="sm" 
          variant="ghost" 
          className="h-8 w-8 p-0 rounded-token-card design-glass-surface hover:design-glass-surface-elevated border border-warning\/20 hover:border-warning\/40 text-warning hover:text-warning-light transition-all duration-200"
          onClick={() => navigate('/shop')}
          aria-label={t('balance.topUp')}
          title={t('balance.topUp')}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </HStack>
  );
};
